import { getUnixTime } from 'date-fns'

import { normalizeAvatar } from 'tools/Files/normalize'
import { freeze } from 'utils/dom'
import { intcmp, strcmp } from 'utils/string'
import { fromNowShort, parseISO } from 'utils/time'

import { normalizeProject } from 'libreon/Project/normalize'

import { defaultUser } from './defs'

// input 'key' and value, always return new dictionary values that
// are to be merged into parent dictionary
export function normalizeUserParam(key, value) {
  switch (key) {
    case 'data': {
      // TODO:
      // * switch address to 'location' or 'locale'
      // * switch profile to ... something else
      const data = {
        dataTypes: {
          tags: { value: {} },
          profile: { value: {} },
          address: { value: {} },
          toggles: { value: {} },
          ...(value || []).reduce((a, d) => {
            a[d.type] = d
            return a
          }, {})
        }
      }
      data.toggles = data.dataTypes.toggles.value
      return data
    }

    case 'tags':
      return {
        tags: value,
        tagsD: normalizeUserTags(value)
      }

    case 'emails': {
      const emails = value
      if (emails && emails.length > 0) {
        const vemails = emails.filter((e) => e.verified)
        const verified = vemails.length > 0
        const primaryEmail =
          (emails.find((email) => email.primary) || vemails[0] || emails[0])
            ?.address || ''
        return { verified, primaryEmail, emails: emails.toSorted(sortEmails) }
      }
      return {}
    }

    default:
      return { [key]: value }
  }
}

const statuses = {
  pending: 1,
  verified: 0,
  bouncing: 2,
  complaint: 3,
  rejected: 4
}

function sortEmails(a, b) {
  return (
    intcmp(statuses[a.status], statuses[b.status]) || strcmp(a.address, b.address)
  )
}

////////////////////////////////////////////////////////////////////////////////
// revise/replace w/newer tags2 stuff
export function normalizeUserTags(value) {
  const tagsD = (value || []).reduce((acc, tag) => {
    const current = acc[tag.type] || []
    // collapse usermap and tag
    const meta = { ...(tag.meta || {}), ...(tag.tag.meta || {}) }
    acc[tag.type] = current.concat(
      Object.freeze({
        ...tag,
        ...tag.tag,
        meta,
        userTagId: tag.id
      })
    )

    return acc
  }, {})

  // sort things
  return Object.keys(tagsD).reduce((acc, k) => {
    acc[k] = tagsD[k].toSorted(
      (a, b) => intcmp(a.order, b.order) || strcmp(a.label, b.label)
    )
    return acc
  }, {})
}

////////////////////////////////////////////////////////////////////////////////
// this is for the internal user/edit state reducer
export function normalizeUser(state, update = {}) {
  // do it first to merge in any changes for emails & data
  const user = { ...state, ...update }

  // on data load action is 'SUMMARY' which gives us this data
  if (user.messages) {
    user.messageSummary = freeze({ ...user.messages?.meta })
    user.messages = undefined
  }
  if (user.projects) {
    user.projectList = freeze(
      user.projects
        .map((pm) => normalizeProject(pm.project))
        .toSorted(
          (a, b) =>
            intcmp(b.releasedAt_t, a.releasedAt_t) ||
            intcmp(b.updatedAt_t, a.updatedAt_t)
        )
    )
  }

  let roles = new Set()
  let actions = new Set()

  if (user.access) {
    if (user.access.roles) roles = new Set(user.access.roles)
    /// NOTE: this is only global accesses, not domain/scoped
    if (user.access.actions) actions = new Set(user.access.actions)
  }

  if (user.tallys) {
    user.tallys = user.tallys.reduce((acc, { type, total }) => {
      if (type === 'points') {
        // externally
        type = 'sparks'
      }
      acc[type] = total
      return acc
    }, {})
  }
  const authStatus = (user ? user.authStatus : undefined) || 'unknown'
  return freeze({
    ...defaultUser(),
    ...user,
    roles,
    actions,
    authStatus,
    ...normalizeUserParam('emails', user.emails),
    ...normalizeUserParam('data', user.data),
    ...normalizeUserParam('tags', user.tags),
    discuss: normalizeUserDiscuss(user.ud),
    canGlobally: (action) => actions.has(action),
    canSelfOrGlobal: (actor, action) =>
      actor.id === user.id || actor.canGlobally(action),
    // canGloballyAny: (actionList) => !actions.isDisjointFrom(new Set(actionList)),
    is: (role) => roles.has(role),
    isAny: (roleList) => !roles.isDisjointFrom(new Set(roleList)),
    avatar: normalizeAvatar(user.avatar.path, user.id),
    age: (Date.now() - new Date(user.insertedAt).getTime()) / 86400000,
    createdAgo: fromNowShort(parseISO(user.insertedAt)),
    lastSeenAgo: fromNowShort(parseISO(user.lastSeen)),
    isIdentified: authStatus !== 'unknown',
    isAuthN: ['authed', 'multi-authed'].includes(authStatus),
    _last: getUnixTime(parseISO(user.lastSeen)),
    _loadedAt: Date.now(),
    _normal: true
  })
}

function normalizeUserDiscuss(ud) {
  return { messages: [], posts: [] }
}

// export function normalizeUser(user, update={}) {
//   // shunt to avoid excess work
//   if (!user || user._normal) return user
//
//   // wants "allows" ?
//   return normalizeUserState(user, update)
// }
